import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Loader2, Shield, Search, Trash2, RefreshCw,
  CheckCircle2, AlertTriangle, Lock, PauseCircle, PlayCircle, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;
const ADMIN_KEY = "Lokesh@123";

export default function CleanDuplicates() {
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);

  const handleLogin = () => {
    if (password === ADMIN_KEY) {
      setAuthenticated(true);
      toast.success("Authenticated!");
    } else {
      toast.error("Invalid password");
    }
  };

  const pollStatus = async () => {
    try {
      const res = await fetch(`${API}/mock-tests/clean/status?admin_key=${ADMIN_KEY}`);
      const data = await res.json();
      setStatus(data);
      if (!data.running) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    } catch { /* silent */ }
  };

  // On login, immediately load status (resumes display of any persisted state)
  useEffect(() => {
    if (authenticated) {
      pollStatus();
    }
  }, [authenticated]);

  // Auto-poll if running
  useEffect(() => {
    if (status?.running && !pollRef.current) {
      pollRef.current = setInterval(pollStatus, 3000);
    }
    return () => {
      if (pollRef.current && !status?.running) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [status?.running]);

  const startScan = async () => {
    try {
      const res = await fetch(`${API}/mock-tests/clean/scan?admin_key=${ADMIN_KEY}`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        toast.success("Scan started!");
        setStatus({ running: true, paused: false, done: false, progress: "Starting scan...", results: [], replaced: 0, total: 0 });
        pollRef.current = setInterval(pollStatus, 3000);
      } else {
        toast.error(data.message || "Failed to start scan");
      }
    } catch {
      toast.error("Failed to start scan");
    }
  };

  const startReplace = async () => {
    try {
      const res = await fetch(`${API}/mock-tests/clean/replace?admin_key=${ADMIN_KEY}`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        toast.success("Replacement started!");
        setStatus(prev => ({ ...prev, running: true, paused: false, done: false }));
        pollRef.current = setInterval(pollStatus, 3000);
      } else {
        toast.error(data.message || "Failed to start replacement");
      }
    } catch {
      toast.error("Failed to start replacement");
    }
  };

  const pauseReplace = async () => {
    try {
      const res = await fetch(`${API}/mock-tests/clean/pause?admin_key=${ADMIN_KEY}`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        toast.info("Pause signal sent — will stop after current batch");
      } else {
        toast.error(data.message || "Failed to pause");
      }
    } catch {
      toast.error("Failed to send pause signal");
    }
  };

  const resumeReplace = async () => {
    try {
      const res = await fetch(`${API}/mock-tests/clean/resume?admin_key=${ADMIN_KEY}`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        toast.success("Resumed replacement!");
        setStatus(prev => ({ ...prev, running: true, paused: false, done: false }));
        pollRef.current = setInterval(pollStatus, 3000);
      } else {
        toast.error(data.message || "Failed to resume");
      }
    } catch {
      toast.error("Failed to resume");
    }
  };

  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900 border-slate-700">
          <CardHeader className="text-center">
            <Lock className="w-12 h-12 text-amber-400 mx-auto mb-2" />
            <CardTitle className="text-xl text-white">Admin — Clean Duplicates</CardTitle>
            <p className="text-slate-400 text-sm">Enter admin password to access</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              className="bg-slate-800 border-slate-600 text-white"
              data-testid="clean-password"
            />
            <Button onClick={handleLogin} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="clean-login-btn">
              <Shield className="w-4 h-4 mr-2" /> Authenticate
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalDups = status?.results?.reduce((s, r) => s + r.duplicate_count, 0) || 0;
  const replaced = status?.replaced || 0;
  const total = status?.total || 0;
  const progressPct = total > 0 ? Math.round((replaced / total) * 100) : 0;

  const isRunning = status?.running;
  const isPaused = !status?.running && status?.paused;
  const hasScanResults = status?.results?.length > 0;
  const isQCPhase = isRunning && status?.qc_phase;
  const replaceInProgress = isRunning && !isQCPhase && (replaced > 0 || status?.progress?.includes("Replacing") || status?.progress?.includes("Starting replacement"));

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header + Controls */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Clean Duplicate Questions</h1>
            <p className="text-slate-400 text-sm">Scan all categories, subjects, and mocks — replace duplicates in batches of 10</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            {/* Scan button — only when not running */}
            {!isRunning && (
              <Button
                onClick={startScan}
                disabled={isRunning}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="scan-btn"
              >
                <Search className="w-4 h-4 mr-2" /> Scan All Mocks
              </Button>
            )}

            {/* Replace button — shown after scan, not running, not paused */}
            {hasScanResults && !isRunning && !isPaused && (
              <Button
                onClick={startReplace}
                variant="destructive"
                data-testid="replace-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Replace {totalDups} Duplicates
              </Button>
            )}

            {/* Pause button — shown while replacement is running */}
            {isRunning && replaceInProgress && (
              <Button
                onClick={pauseReplace}
                variant="outline"
                className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
                data-testid="pause-btn"
              >
                <PauseCircle className="w-4 h-4 mr-2" /> Pause
              </Button>
            )}

            {/* Resume button — shown when paused */}
            {isPaused && hasScanResults && (
              <Button
                onClick={resumeReplace}
                className="bg-green-600 hover:bg-green-700"
                data-testid="resume-btn"
              >
                <PlayCircle className="w-4 h-4 mr-2" /> Resume
              </Button>
            )}
          </div>
        </div>

        {/* Progress Bar (replacement only) */}
        {total > 0 && (
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isQCPhase && <ShieldCheck className="w-4 h-4 text-emerald-400 animate-pulse" />}
                  {isRunning && !isQCPhase && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
                  {isPaused && <PauseCircle className="w-4 h-4 text-amber-400" />}
                  {status?.done && !isRunning && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                  <span className="text-sm font-medium text-slate-200">
                    {isPaused ? "Paused" : isQCPhase ? "Auto-QC Running..." : isRunning ? "Replacing..." : status?.done ? "Done" : "Ready"}
                  </span>
                </div>
                <span className="text-lg font-bold text-white" data-testid="replace-counter">
                  {replaced} <span className="text-slate-400 font-normal text-sm">/ {total} replaced</span>
                </span>
              </div>
              <Progress value={progressPct} className="h-2 bg-slate-700" />
              <p className="text-xs text-slate-400">{progressPct}% complete</p>
            </CardContent>
          </Card>
        )}

        {/* Status message */}
        {status?.progress && (
          <Card className={`border-slate-700 ${isQCPhase ? "bg-emerald-950/40" : "bg-slate-900"}`}>
            <CardContent className="p-4 flex items-center gap-3">
              {isQCPhase && <ShieldCheck className="w-5 h-5 text-emerald-400 animate-pulse flex-shrink-0" />}
              {isRunning && !isQCPhase && <Loader2 className="w-5 h-5 text-blue-400 animate-spin flex-shrink-0" />}
              {isPaused && <PauseCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />}
              {status?.done && !isRunning && <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />}
              <span className={`text-sm ${isQCPhase ? "text-emerald-300" : "text-slate-300"}`}>{status.progress}</span>
            </CardContent>
          </Card>
        )}

        {/* Scan Results */}
        {hasScanResults && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span className="text-white font-semibold">
                Found {totalDups} duplicate(s) across {status.results.length} subject(s)
              </span>
            </div>

            {status.results.map((entry, ei) => (
              <Card key={ei} className="bg-slate-900 border-slate-700">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge className="bg-blue-900 text-blue-300">{entry.exam_type.toUpperCase()}</Badge>
                    <Badge className="bg-purple-900 text-purple-300">{entry.subject.replace(/_/g, " ")}</Badge>
                    <span className="text-slate-400 text-sm">{entry.total_questions} total questions</span>
                    <Badge className="bg-red-900 text-red-300">{entry.duplicate_count} duplicates</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {entry.duplicates.slice(0, 10).map((dup, di) => (
                    <div key={di} className="bg-slate-800/60 rounded-lg p-3 border border-slate-700 space-y-1">
                      <div className="flex items-start gap-2">
                        <Trash2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs text-red-400 font-mono">{dup.dup_doc_id} — Q#{dup.dup_q_idx + 1} ({dup.dup_type})</p>
                            {dup.within_doc && (
                              <span className="text-xs bg-orange-900/60 text-orange-300 px-1.5 py-0.5 rounded font-semibold">Within-mock duplicate</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-300 truncate">{dup.dup_text}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 ml-6">
                        <span className="text-xs text-slate-500">Similar to:</span>
                        <span className="text-xs text-slate-400 truncate">{dup.orig_doc_id} — Q#{(dup.orig_q_idx ?? 0) + 1} "{dup.orig_text}"</span>
                      </div>
                    </div>
                  ))}
                  {entry.duplicates.length > 10 && (
                    <p className="text-xs text-slate-500 text-center">... and {entry.duplicates.length - 10} more</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {status?.done && !hasScanResults && (
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-300">No Duplicates Found!</h3>
              <p className="text-slate-400 text-sm">All mock test questions are unique across all categories and subjects.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
